import { useEffect, useRef } from 'react'

import useAuth from '@/hooks/useAuth'
import { identifyUser, resetPostHog } from '@/lib/posthog'

/**
 * Keeps the PostHog identity in step with the auth session. Renders nothing.
 *
 * Driven off auth *state* rather than the `login`/`logout` callbacks on purpose:
 * a session can begin several ways (password login, Microsoft OAuth redirect,
 * or a cookie restored on page load) and watching the resolved state catches all
 * of them without touching auth logic.
 *
 * Mounted inside `AuthProvider`. No-op when PostHog is not initialised.
 */
export default function PostHogIdentity() {
    const { isAuthenticated, isInitialized, user } = useAuth()
    // Avoids re-identifying the same user on every unrelated auth-state change.
    const identifiedRef = useRef(null)

    useEffect(() => {
        if (!isInitialized) {
            return
        }

        if (isAuthenticated && user?.id) {
            const id = String(user.id)
            if (identifiedRef.current !== id) {
                identifyUser(user)
                identifiedRef.current = id
            }
            return
        }

        // Only reset on a real sign-out, not on the initial anonymous load —
        // reset() would discard the anonymous distinct_id and with it the
        // pre-login pageviews that identify() is supposed to merge.
        if (identifiedRef.current !== null) {
            resetPostHog()
            identifiedRef.current = null
        }
    }, [isAuthenticated, isInitialized, user])

    return null
}
